import { useEquipmentStore } from "@/frontend/store/EquipmentStore";
import { useEffect, useRef, useState } from "react";
import { CollapseButton } from "../buttons/CollapseButton";
import { I18n } from "@/i18n/I18n";
import { useProcedureInstanceStore } from "@/frontend/store/ProcedureInstanceStore";
import { capitalize } from "lodash";
import { LoadingSpinner } from "../LoadingSpinner";
import { ConfigurationCell } from "./ConfigurationCell";
import { EquipmentConfigModal } from "./EquipmentConfigModal";
import { EditEquipmentModal } from "./EditEquipmentModal";
import { CreateEquipmentButton } from "./CreateEquipmentModal";
import { DeleteButton } from "../buttons/DeleteButton";
import { EquipmentItem } from "@/backend/models/equipmentItem";
import { EquipmentGroupSelectCell } from "./EquipmentGroupSelectCell";
import { InstrumentCell } from "./InstrumentCell";
import { nativeBridge } from "@/frontend/embed/nativeBridge";

export function EquipmentTable(props: { editable?: boolean, parentShowCascade?: boolean }) {
  const { editable, parentShowCascade } = props;
  const equipmentList = useEquipmentStore(state => state.equipmentList);
  const [showTable, setShowTable] = useState<boolean>(parentShowCascade === undefined ? true : parentShowCascade);

  useEffect(() => {
    setShowTable(parentShowCascade === undefined ? true : parentShowCascade);
  }, [parentShowCascade]);

  const collapsedClass = showTable ? '' : 'border-bottom-0';

  if (!editable && equipmentList.length === 0) {
    return null;
  }

  return (
    <div className="card">
      <div className={`card-header d-flex ${collapsedClass} justify-content-between`}>
        <div className='d-flex'>
          <CollapseButton showElement={showTable} setShowElement={setShowTable} title={I18n.get('requiredEquipment')} />
          <h5 className="card-title my-auto">{I18n.get('requiredEquipment')}</h5>
        </div>
      </div>
      { showTable &&
        <div className='card-body'>
          <CurrentInstrumentTable showDescription isEditable={!!editable} equipment={equipmentList} />
          { editable && <CreateEquipmentButton /> }
        </div>
      }
    </div>
  );
}

export function CurrentInstrumentTable(props: { showDescription: boolean, isEditable: boolean, equipment: EquipmentItem[] }) {
  const { showDescription, isEditable, equipment } = props;
  const deleteAction = useEquipmentStore(state => state.deleteEquipment);
  const procedureInstance = useProcedureInstanceStore(state => state.procedureInstance);
  const [headerCount, setHeaderCount] = useState(5);
  const headerRowRef = useRef<HTMLTableRowElement>(null);
  const loadingEquipmentList = useEquipmentStore(state => state.loadingEquipmentList);

  /**
   * Only on the run view, and only inside the desktop app. Editing a template
   * is not when equipment gets connected, and in a browser there are no
   * instruments to offer -- an empty column on every row would say nothing.
   */
  const showInstruments = !isEditable && nativeBridge.isAvailable();

  useEffect(() => {
    if (headerRowRef.current) {
      const headerCells = headerRowRef.current.querySelectorAll('th');
      setHeaderCount(headerCells.length);
    }
  }, [showDescription, isEditable, showInstruments]);

  return (
    <table className='table table-hover max-w-100"'>
    <thead>
      <tr ref={headerRowRef}>
        <th>{I18n.get('name')}</th>
        { showDescription && <th>{I18n.get('description')}</th> }
        { isEditable && <th>{I18n.get('group')}</th> }
        { showInstruments && <th>{I18n.get('instrument')}</th> }
        <th>{capitalize(I18n.get('configuration'))}</th>
        { isEditable && <th>{I18n.get('actions')}</th> }
      </tr>
    </thead>
    <tbody>
      { loadingEquipmentList && <tr><td className='text-center' colSpan={headerCount}><LoadingSpinner /></td></tr> }
      { equipment.length > 0 ? equipment.map((item) => {
        return (
          <tr key={item.id} >
            <td className="text-wrap w-20 text-break">{isEditable ? `${item.name} (${item.identifier})` : item.name}</td>
            { showDescription && <td className="text-wrap w-40 text-break">{item.description}</td> }
            { isEditable && (
              <td className="align-middle">
                <EquipmentGroupSelectCell equipmentId={item.id} />
              </td>
            )}
            { showInstruments && (
              <td className="align-middle">
                <InstrumentCell equipment={item} />
              </td>
            )}
            <td>
              <ConfigurationCell item={item} />
            </td>
            { isEditable &&
              <>
                <td aria-label='actions' >
                  <div className='d-flex align-items-start gap-2'>
                    { (item.defaultConfiguration && procedureInstance?.isTemplate()) &&
                      <EquipmentConfigModal item={item} />
                    }
                    <EditEquipmentModal equipmentItem={item} />
                    <DeleteButton
                      model={I18n.get('equipment')}
                      onDelete={() => deleteAction(item)}
                      title={item.name}
                    />
                  </div>
                </td>
              </>
            }
          </tr>
        );
      }): <tr><td className='text-center' colSpan={headerCount}>{I18n.get('noEquipment')}</td></tr>}
    </tbody>
  </table>
  );
}
